<template>
  <div class="country-selector">
    <h3
      v-if="!hideMode"
      class="h3"
    >
      Countries access policy
    </h3>

    <md-progress-bar
      v-if="loading"
      md-mode="indeterminate"
    />
    <div
      v-else
      class="node-form__tags tags"
    >
      <div
        v-if="!hideMode"
        class="top-line"
      >
        <div>
          <md-radio
            v-model="mode"
            value="common"
            class="md-primary"
            @change="modeChanged(mode)"
          >
            Common
          </md-radio>
          <md-radio
            v-model="mode"
            value="blacklist"
            class="md-accent"
            @change="modeChanged(mode)"
          >
            Blacklist
          </md-radio>
          <md-radio
            v-model="mode"
            value="whitelist"
            class="md-primary"
            @change="modeChanged(mode)"
          >
            Whitelist
          </md-radio>
        </div>

        <div v-if="withCopyPaste">
          <button
            class="copy-button"
            @click="onCopyClick"
          >
            <md-icon>content_copy</md-icon>
          </button>

          <button
            class="copy-button"
            @click="onPasteClick"
          >
            <md-icon>content_paste</md-icon>
          </button>
        </div>
      </div>

      <grouped-auto-complete
        v-if="mode !== 'common'"
        :groups="groups"
        :selected="selectedCountryNames"
        :loading="loading"
        :label="showLabel ? 'Country' : null"
        :show-selected-bubbles="showGroupedBubbles"
        :search-options="searchOptions"
        @list-updated="onCountryListUpdate"
      />

      <p v-if="mode==='blacklist' && !hideMode">
        Enable shortcuts for all countries except:
      </p>
      <p v-if="mode==='whitelist' && !hideMode">
        Disable shortcuts for all countries except:
      </p>

      <div v-if="!showGroupedBubbles">
        <md-chip
          v-for="country in selectedCountryNames"
          :key="country"
          class="md-primary"
          md-deletable
          @click="removeCountry(country)"
        >
          {{ country }}
        </md-chip>
      </div>
    </div>
  </div>
</template>

<script>
import {countryNameToCode, countryOptions} from "@/assets/countries_iso/all-iso-codes";
import {regionCountryLists} from "@/assets/countries_iso/regional-iso-codes";

import GroupedAutoComplete from "@/components/Forms/Fields/GroupedAutoComplete";

import CopyPaster from "../../../utils/copyPaster";

const copyPaster = new CopyPaster("country-selector-product");

export default {
    name: "CountrySelectorWithContinents",
    components: {
        GroupedAutoComplete
    },
    props: {
        "initial-mode": { type: String, default: "" },
        "initial-iso-codes": { type: Array, default() { return []; } },
        loading: Boolean,
        hideMode: Boolean,
        showLabel: Boolean,
        showGroupedBubbles: Boolean,
        withCopyPaste: Boolean,
    },
    data() {
        return {
            mode: "common",
            groups: regionCountryLists,
            selectedCountryNames: [],
            selectedISOCodes: [],

            searchOptions: countryOptions
        };
    },
    watch: {
        "initialMode": "updateFromProps",
        "initialIsoCodes": "updateFromProps",
    },
    mounted() {
        // if props are passed, use them as the initial state
        if (this.$props.initialMode || this.$props.initialIsoCodes) {
            this.updateFromProps();
        }

        if (this.$props.hideMode) {
            // setting mode to 'blacklist' so the country field is shown
            this.mode = "blacklist";
        }
    },
    methods: {
        modeChanged() {
            this.$emit("modeChanged", this.mode);
        },
        updateFromProps() {
            this.mode =  this.$props.initialMode;
            this.selectedISOCodes = this.$props.initialIsoCodes;

            const countryKeys = Object.keys(countryNameToCode);
            this.selectedCountryNames = this.$props.initialIsoCodes.map(
                code => countryKeys.find(key => countryNameToCode[key] === code)
            );
        },
        onCountryListUpdate(newList) {
            this.selectedCountryNames = newList;
            this.selectedISOCodes = newList.map(country => countryNameToCode[country]);

            this.emitUpdate();
        },
        removeCountry(countryName) {
            this.selectedCountryNames = this.selectedCountryNames.filter(el => {
                return el !== countryName;
            });
            this.selectedISOCodes = this.selectedISOCodes.filter(el => {
                return el !== countryNameToCode[countryName];
            });

            this.emitUpdate();
        },
        emitUpdate() {
            this.$emit("countryListChanged", this.selectedISOCodes);
        },
        onCopyClick() {
            this.$notify({
                group: "foo",
                title: "Country settings copied",
            });
            copyPaster.copy({
                mode: this.mode,
                countryISOCodes: this.selectedISOCodes,
                countryList: this.selectedCountryNames
            });
        },
        onPasteClick() {
            const { mode, countryISOCodes, countryList } = copyPaster.paste();

            if (!(mode && countryList && countryISOCodes)) {
                this.$notify({
                    group: "foo",
                    title: "Couldn't paste, smth went wrong, did you copy first?",
                });
                return;
            }

            this.mode = mode;
            this.selectedCountryNames = countryList;
            this.selectedISOCodes = countryISOCodes;
            this.modeChanged();
            this.emitUpdate();
            this.$notify({
                group: "foo",
                title: "Country settings applied"
            });
        }
    }
};
</script>
<style lang="scss">
    .country-picker-wrapper {
        display: flex;
        align-items: center;

        .add-county-button {
            height: 35px !important;
        }
    }

    .md-list-item-container.md-button-clean {
        .md-list-item-content.md-ripple {
            padding: 0 !important;
        }
    }

    .selector-option {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .region-option {
        margin: 0 5px;
        padding-right: 5px;
        background-color: #43a047;
        color: white;
        .md-checkbox.md-theme-default {
            margin: 0 5px 0 10px !important;
            background-color: white;
            .md-checkbox-container {
                border-color: white;
            }
        }
    }

    .country-option {
        .md-checkbox.md-theme-default {
            margin: 0 5px 0 15px !important;
        }
        .md-highlight-text {
            font-size: 12px;
        }
    }

    .md-menu-content-bottom-start.md-menu-content-small.md-menu-content.md-theme-default {
        width: 300px;
        min-width: 300px;
    }

    .top-line {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .copy-button {
        padding: 5px;
        border-radius: 8px;
        border: 2px solid gray;
        cursor: pointer;
        opacity: .8;

        transition: opacity 100ms ease-out;
        &:hover {
            opacity: .9;
        }
        &:active {
            opacity: 1;
        }

        i {
            width: 12px;
            height: 12px;
        }

        & + & {
            margin-left: 5px;
        }
    }
</style>
